/**
 * SubmitSection component for the dish form
 */

import React from 'react';
import { getTranslation } from '../../utils/translate.js';
import { DISH_STATUS } from '../../utils/dishFormUtils.js';
import Button from '../comman/button.js';

/**
 * Spacing constants for consistent layout
 * Using 8px increments (0.5rem = 8px in Tailwind)
 */
const SPACING = {
  // Container styles
  CONTAINER: 'mt-8 bg-white dark:bg-[#161b22] rounded-lg shadow-sm p-6 flex flex-col items-center border dark:border-[#30363d]',

  // Button styles
  BUTTON: 'text-white dark:text-[#c9d1d9] text-sm bg-customBackground dark:bg-[#21262d] hover:bg-customHoverBlack dark:hover:bg-[#30363d] px-6 py-3 rounded-md transition-all duration-200',
  BUTTON_ACTIVE: 'hover:shadow-lg hover:scale-105',
  BUTTON_DISABLED: 'opacity-70 cursor-not-allowed',
  BUTTON_FOCUS: 'focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500',
  BUTTON_CONTENT: 'flex items-center justify-center gap-2 w-full md:w-auto max-w-xs mx-auto',

  // Notice styles
  PENDING_NOTICE: 'mt-8 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg shadow-sm p-6',
  PENDING_TEXT: 'text-sm text-yellow-700 dark:text-yellow-400 flex items-center gap-2 font-medium',
};


/**
 * Check icon component
 * @returns {JSX.Element} Check icon SVG
 */
const CheckIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    className="h-5 w-5" 
    fill="none"
    viewBox="0 0 24 24" 
    stroke="currentColor"
    aria-hidden="true"
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

/**
 * Warning icon component
 * @returns {JSX.Element} Warning icon SVG
 */
const WarningIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    className="h-5 w-5 mr-2 text-yellow-500" 
    fill="none"
    viewBox="0 0 24 24" 
    stroke="currentColor"
    aria-hidden="true"
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

/**
 * SubmitSection component
 * @param {Object} props - Component props
 * @returns {JSX.Element} SubmitSection component
 */
const SubmitSection = ({
  formData,
  isSubmitting,
  handleSubmit,
  language
}) => {
  // If dish is in pending status, show pending notice instead of submit button
  if (formData.status === DISH_STATUS.PENDING) {
    return (
      <div className={SPACING.PENDING_NOTICE}>
        <p className={SPACING.PENDING_TEXT}>
          <WarningIcon />
          {getTranslation('menu.pendingEditNotice', language)}
        </p>
      </div>
    );
  }

  // Otherwise, show submit button
  return (
    <div className={`${SPACING.CONTAINER} pb-6 sm:pb-8`}>
      <Button
        variant="primary"
        size="lg"
        disabled={isSubmitting}
        loading={isSubmitting}
        onClick={handleSubmit}
        className={`${SPACING.BUTTON_CONTENT} ${isSubmitting ? '' : 'hover:shadow-lg hover:scale-105'}`}
        aria-busy={isSubmitting}
      >
        {!isSubmitting && <CheckIcon />}
        {isSubmitting ? (getTranslation('common.processing', language) || 'Processing...') : getTranslation('common.confirm', language)}
      </Button>
    </div>
  );
};

export default SubmitSection;
